import { db } from "@/lib/db";
import { isExcludedBrand } from "@/lib/excluded-brands";

/**
 * Single source of truth for "who am I and who are my competitors".
 *
 * Prospect/outreach scans write throwaway companies + competitors into an
 * account, tagged `business_context.is_prospect_scan`. Those rows must never
 * be visible on a customer-facing screen — otherwise a real-estate account
 * ends up being told that "Together AI is gaining ground".
 */

export type ActiveEntity = {
  id: string;
  name: string;
  website_url: string | null;
  /** This entity's own name variants; counts use only these. */
  aliases?: string[];
  /** Raw business_context JSONB (specialties, hidden_ai_competitors, etc.). */
  business_context?: Record<string, unknown> | null;
};

export type ActiveEntities = {
  own: ActiveEntity | null;
  competitors: ActiveEntity[];
  /** Normalized competitor names, for matching against alert rows. */
  competitorKeys: Set<string>;
};

/** Normalize a name or URL so "https://www.foo.com/" === "foo.com". */
export function normEntityKey(s: string | null | undefined): string {
  return (s ?? "")
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .replace(/^www\./, "")
    .replace(/\/+$/, "");
}

/** Human-readable brand label for stored names, including domain-only records. */
export function entityLabel(s: string | null | undefined): string {
  const raw = (s ?? "").trim();
  if (!raw) return "";
  const cleaned = raw
    .replace(/^https?:\/\//i, "")
    .replace(/^www\./i, "")
    .replace(/\/.*$/, "");
  if (!/^[a-z0-9-]+(?:\.[a-z0-9-]+)+$/i.test(cleaned)) return raw;

  const hostParts = cleaned.split(".");
  const multiPartCountryTld = hostParts.length > 2 && hostParts.at(-1)?.length === 2 && hostParts.at(-2)?.length === 2;
  const base = hostParts.slice(0, multiPartCountryTld ? -2 : -1).at(-1) ?? hostParts[0];
  const brand = base
    .replace(/^(try|get|use|go|join|hey|meet)(?=[a-z0-9]{3,})/i, "")
    .replace(/(?:app|hq)$/i, "") || base;
  return brand
    .split(/[-_]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function isProspectRow(row: { business_context: unknown }): boolean {
  const bc = row.business_context as Record<string, unknown> | null;
  return !!bc && (bc.is_prospect_scan === true || !!bc.untracked_at);
}

export async function loadActiveEntities(userId: string): Promise<ActiveEntities> {
  // Filter prospect rows in SQL, not in JS. Admin accounts accumulate
  // thousands of prospect-scan entities, and PostgREST caps a response at
  // 1000 rows — the real competitors (created long ago) fell outside that
  // window, so the briefing reported "No competitors tracked".
  const { data } = await db()
    .from("tracked_entities")
    .select("id, name, type, website_url, business_context, created_at, aliases")
    .eq("user_id", userId)
    .in("type", ["own_company", "competitor"])
    .or(NON_PROSPECT_OR)
    .order("created_at", { ascending: false });

  const rows = (data ?? []).filter((r) => !isProspectRow(r));

  const own = rows.find((r) => r.type === "own_company") ?? null;
  const competitors = rows
    .filter((r) => r.type === "competitor" && !isExcludedBrand(r.name) && !isExcludedBrand(r.website_url))
    .map((r) => ({
      id: r.id,
      name: r.name,
      website_url: r.website_url ?? null,
      aliases: ((r as any).aliases ?? []) as string[],
      business_context: (r.business_context ?? null) as Record<string, unknown> | null,
    }));

  // Who-counts decisions for this account; tracked competitors always count.
  const { loadRelevanceRows, setRelevanceOverrides } = await import("@/lib/relevance-overrides");
  const { entityKeys } = await import("@/lib/named-providers");
  const relRows = await loadRelevanceRows(userId).catch(() => []);
  setRelevanceOverrides(relRows, competitors.flatMap((c) => [...entityKeys(c)]));

  return {
    own: own
      ? {
          id: own.id,
          name: own.name,
          website_url: own.website_url ?? null,
          business_context: (own.business_context ?? null) as Record<string, unknown> | null,
        }
      : null,
    competitors,
    competitorKeys: new Set(
      competitors.flatMap((c) => [normEntityKey(c.name), normEntityKey(c.website_url)]).filter(Boolean),
    ),
  };
}

/**
 * Drop prospect/outreach-scan rows from a raw tracked_entities list. Use this
 * on any customer-facing read that selects entities by user_id.
 */
export function excludeProspectEntities<T extends { business_context?: unknown }>(rows: T[] | null | undefined): T[] {
  return (rows ?? []).filter((r) => {
    const bc = r.business_context as Record<string, unknown> | null | undefined;
    return !(bc && (bc.is_prospect_scan === true || (bc as any).untracked_at));
  });
}

/**
 * PostgREST `.or()` filter that keeps real customer rows and drops
 * prospect-scan rows.
 *
 * Do NOT use `.or(NON_PROSPECT_OR)` —
 * when business_context is NULL the JSON extraction is NULL, `eq` evaluates
 * to NULL, and NOT NULL is still NULL, so ordinary rows get filtered out too
 * (that's what made "No company website found" fire for accounts that clearly
 * had one). Explicitly allowing NULL fixes it.
 */
export const NON_PROSPECT_OR =
  "business_context->>is_prospect_scan.is.null,business_context->>is_prospect_scan.neq.true";
